import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, MapPin, CheckCircle2, ChevronRight } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Pricing } from "@/components/Pricing";
import { ReportsShowcase } from "@/components/ReportsShowcase";
import { Testimonials } from "@/components/Testimonials";
import { FAQ } from "@/components/FAQ";
import { AboutMini } from "@/components/AboutMini";
import { TestPicker } from "@/components/TestPicker";
import { TestComparison } from "@/components/TestComparison";
import { getSuburb } from "@/lib/suburbs";
import { TRUST_BADGES } from "@/lib/siteData";
import { SEO, LANDING } from "@/constants/testIds";
import { SchemaJsonLd, localBusinessSchema, serviceSchema, faqSchema, breadcrumbSchema } from "@/lib/seo";
import { suburbFAQ } from "@/lib/faqs";
import { useSEO, SEO_DEFAULTS } from "@/lib/useSEO";

const HERO_BG_DEFAULT = "/img/scwt-hero-bg.jpg";
// Phase 2L-A36 — calmer rural-hinterland fallback hero used when a
// suburb has `area_type` of rural-acreage / hinterland and no
// per-suburb `hero_image` configured in locations.json.
const HERO_BG_HINTERLAND = "/img/v2/suburb-hinterland.jpg";
const HERO_BG_HINTERLAND_WEBP = "/img/v2/suburb-hinterland.webp";
const BASE_URL = SEO_DEFAULTS.baseUrl;

function suburbHeroSrc(data) {
  // Real per-suburb hero wins (locations.json, when present). Otherwise
  // pick the hinterland fallback for hinterland-flagged suburbs and the
  // legacy coastal hero everywhere else.
  if (data?.hero_image?.src) return { src: data.hero_image.src, webp: null };
  const area = (data?.kind || data?.area_type || data?.region_cluster || "").toLowerCase();
  if (area.includes("rural") || area.includes("hinterland")) {
    return { src: HERO_BG_HINTERLAND, webp: HERO_BG_HINTERLAND_WEBP };
  }
  return { src: HERO_BG_DEFAULT, webp: null };
}

export default function SuburbLanding() {
  const { suburb } = useParams();
  const data = getSuburb(suburb);

  const qas = data ? suburbFAQ(data.name) : [];
  const pageUrl = data ? `${BASE_URL}/water-testing/${data.slug}` : BASE_URL;

  useSEO({
    title: data ? `Water Testing ${data.name} — Tank, Bore & Drinking Water Analysis` : "Water Testing",
    description: data
      ? `Local water testing laboratory serving ${data.name}, QLD ${data.postcode}. Tank, bore & drinking water analysis with plain-English summary reports. One flat all-inclusive price nationwide.`
      : "Local water testing across the Sunshine Coast.",
    url: pageUrl,
  });

  if (!data) return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen bg-white">
      <SchemaJsonLd data={localBusinessSchema()} id="jsonld-localbusiness" />
      <SchemaJsonLd
        data={serviceSchema(
          `Water Testing in ${data.name}`,
          `Professional local water testing laboratory serving ${data.name}, QLD ${data.postcode}. Rainwater tank, bore water and drinking water analysis with plain-English reports.`,
          data.name,
        )}
        id="jsonld-service"
      />
      <SchemaJsonLd data={faqSchema(qas)} id="jsonld-faq" />
      <SchemaJsonLd
        data={breadcrumbSchema([
          { name: "Home", url: BASE_URL },
          { name: "Popular Locations", url: `${BASE_URL}/#areas` },
          { name: data.name, url: pageUrl },
        ])}
        id="jsonld-breadcrumb"
      />

      <Header />
      <main>
        {/* Breadcrumb */}
        <nav aria-label="Breadcrumb" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 lg:pt-32">
          <ol className="flex flex-wrap items-center gap-1.5 text-xs text-slate-500">
            <li><Link to="/" className="hover:text-slate-900 transition-colors">Home</Link></li>
            <li><ChevronRight className="w-3 h-3" /></li>
            <li><Link to="/#areas" className="hover:text-slate-900 transition-colors">Popular Locations</Link></li>
            <li><ChevronRight className="w-3 h-3" /></li>
            <li className="font-semibold text-slate-900">{data.name}</li>
          </ol>
        </nav>

        {/* Hero */}
        <section data-testid={SEO.suburbHero} className="relative overflow-hidden pt-10 lg:pt-14 pb-20 lg:pb-28">
          <div className="absolute inset-0 -z-10">
            {(() => {
              const hero = suburbHeroSrc(data);
              return (
                <picture>
                  {hero.webp && <source srcSet={hero.webp} type="image/webp" />}
                  <img
                    src={hero.src}
                    alt=""
                    aria-hidden="true"
                    loading="eager"
                    decoding="async"
                    className="absolute inset-0 w-full h-full object-cover opacity-80"
                  />
                </picture>
              );
            })()}
            <div className="absolute inset-0 bg-gradient-to-b from-white/85 via-white/65 to-white" />
          </div>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass mb-6"
            >
              <MapPin className="w-3.5 h-3.5 text-cyan-600" />
              <span className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-700">{data.name} · QLD {data.postcode}</span>
            </motion.div>

            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 leading-[1.05] max-w-4xl">
              Water testing in <span className="water-text">{data.name}</span>
            </h1>
            <p className="mt-6 text-lg sm:text-xl text-slate-600 max-w-2xl leading-relaxed">{data.blurb}</p>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link
                data-testid={SEO.suburbBookBtn}
                to="/book-water-test"
                className="btn-aqua group inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full text-base font-semibold"
              >
                Book online — one flat all-inclusive price
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <a
                href="#pricing"
                data-testid={LANDING.heroViewTestsBtn}
                className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full text-base font-semibold text-slate-900 bg-white border-2 border-slate-200 hover:border-slate-900 transition-all"
              >
                View test options
              </a>
            </div>

            <div className="mt-10 grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-3 max-w-3xl">
              {TRUST_BADGES.map((t) => (
                <div key={t} className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 text-cyan-500 flex-shrink-0" />
                  <span className="text-sm text-slate-700 leading-snug">{t}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Suburb-specific content */}
        <section className="py-16 lg:py-24 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-12 gap-10">
              <div className="lg:col-span-7">
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-cyan-600">Local expertise</span>
                <h2 className="font-display mt-3 text-3xl lg:text-4xl font-bold text-slate-900 leading-[1.1]">
                  Why {data.name} homes choose Aware Water Testing
                </h2>
                <div className="mt-6 space-y-4 text-slate-700 text-base leading-relaxed">
                  <p>We've been testing water across {data.name} and the wider Sunshine Coast for years. Our local lab in Noosaville means fast turnaround — you can drop your sample in person and chat directly with the lab team reviewing it.</p>
                  <p>Every {data.name} customer receives a professional Certificate of Analysis paired with a plain-English summary report. You'll know what was found, what it means, and exactly what (if anything) to do next.</p>
                  <p>Whether you're on rainwater tank or bore water, we'll match the right test to your situation. (We don't currently offer town/mains water testing.)</p>
                </div>
              </div>
              <div className="lg:col-span-5">
                <div className="bg-white rounded-3xl border border-slate-100 p-7 shadow-sm">
                  <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-cyan-600">Most common around {data.name}</div>
                  <ul className="mt-4 space-y-3">
                    {["Rainwater tank screening", "Bore water iron & manganese", "pH & corrosion checks", "Bacterial / E. coli analysis", "Annual peace-of-mind testing"].map((it) => (
                      <li key={it} className="flex items-start gap-2.5">
                        <CheckCircle2 className="w-4 h-4 mt-0.5 text-cyan-500 flex-shrink-0" />
                        <span className="text-sm text-slate-700">{it}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        <TestPicker />
        <Pricing />
        <TestComparison />
        <ReportsShowcase />
        <AboutMini suburb={data.name} />
        <Testimonials />

        <FAQ qas={qas} title={`Water testing in ${data.name} — your questions answered`} subtitle="FAQ" testIdPrefix="suburb-faq" />

        {/* Final CTA */}
        <section className="py-20 lg:py-28">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="rounded-[2.5rem] bg-slate-900 p-10 sm:p-16 lg:p-20 overflow-hidden relative">
              <div className="absolute -top-32 -right-32 w-96 h-96 bg-cyan-500/25 rounded-full blur-3xl" />
              <div className="relative max-w-2xl">
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-cyan-400">{data.name} · QLD {data.postcode}</span>
                <h2 className="font-display mt-3 text-3xl sm:text-5xl font-bold text-white leading-[1.05]">Ready to understand your {data.name} water?</h2>
                <p className="mt-4 text-lg text-slate-300">Book online for one flat all-inclusive price and get a plain-English answer about your water within days.</p>
                <Link to="/book-water-test" className="btn-aqua mt-7 inline-flex items-center gap-2 px-7 py-4 rounded-full text-base font-semibold">
                  Book online <ArrowRight className="w-5 h-5" />
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
